import { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchServices,
  fetchRedis,
  fetchQueue,
  fetchStorage,
  fmtBytes,
  fmtUptime,
  INFRA_STR as S,
  type ServiceHealth,
  type RedisStats,
  type QueueStats,
  type StorageStats,
} from './infraApi'

/** Kichik load-hook: abortable fetch + reload. Har A17 ekran shundan foydalanadi. */
function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, err, reload }
}

function RefreshBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
    >
      {S.refresh}
    </button>
  )
}

function Rows({ n = 8 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

/* ── A17.01 — Xizmatlar holati ─────────────────────────────────── */
export function InfraServicesScreen() {
  const { data, err, reload } = useLoad<ServiceHealth[]>(fetchServices)
  return (
    <>
      <PageHeader title={S.services} screenId="A17.01" description={S.servicesDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto grid max-w-3xl gap-2.5 sm:grid-cols-3">
            {data.map((s) => (
              <div key={s.name} className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm uppercase text-secondary">{s.name}</span>
                  <span
                    className={`flex items-center gap-1 text-xs ${s.up ? 'text-success' : 'text-danger'}`}
                  >
                    <span aria-hidden>●</span>
                    {s.up ? S.up : S.down}
                  </span>
                </div>
                <p className="mt-3 text-2xl font-semibold text-primary tabular">
                  {s.latency_ms.toFixed(1)}
                  <span className="ml-1 text-sm font-normal text-tertiary">ms</span>
                </p>
                <p className="mt-1 truncate text-xs text-tertiary">{s.detail || '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.15 — Kesh holati (Redis) ──────────────────────────────── */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <p className="text-xs text-tertiary">{label}</p>
      <p className="mt-0.5 truncate text-lg font-semibold text-primary tabular">{value}</p>
    </div>
  )
}
export function InfraRedisScreen() {
  const { data, err, reload } = useLoad<RedisStats>(fetchRedis)
  return (
    <>
      <PageHeader title={S.redis} screenId="A17.15" description={S.redisDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows />
        ) : (
          <div className="mx-auto grid max-w-4xl gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
            <Stat label={S.version} value={data.version || '—'} />
            <Stat label={S.uptime} value={fmtUptime(data.uptime_seconds)} />
            <Stat label={S.clients} value={String(data.connected_clients)} />
            <Stat label={S.ops} value={String(data.ops_per_sec)} />
            <Stat
              label={S.memory}
              value={data.maxmemory > 0 ? `${fmtBytes(data.used_memory)} / ${fmtBytes(data.maxmemory)}` : fmtBytes(data.used_memory)}
            />
            <Stat label={S.policy} value={data.maxmemory_policy || '—'} />
            <Stat label={S.hitRate} value={`${data.hit_rate.toFixed(1)}%`} />
            <Stat label={S.keys} value={data.db_keys.toLocaleString()} />
            <Stat label={S.evicted} value={data.evicted_keys.toLocaleString()} />
            <Stat label={S.expired} value={data.expired_keys.toLocaleString()} />
            <Stat label={S.frag} value={data.frag_ratio.toFixed(2)} />
            <Stat label={S.aof} value={data.aof_enabled ? 'on' : 'off'} />
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.16 — Fayl saqlash (MinIO) ─────────────────────────────── */
export function InfraStorageScreen() {
  const { data, err, reload } = useLoad<StorageStats>(fetchStorage)
  return (
    <>
      <PageHeader title={S.storage} screenId="A17.16" description={S.storageDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={4} />
        ) : (
          <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-border-default">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-2 text-xs text-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">{S.bucket}</th>
                  <th className="px-4 py-2 font-medium">{S.reachable}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.objects}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.size}</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((b) => (
                  <tr key={b.name} className="border-t border-border-subtle">
                    <td className="px-4 py-2 font-mono text-primary">{b.name}</td>
                    <td className={`px-4 py-2 ${b.reachable ? 'text-success' : 'text-danger'}`}>
                      {b.reachable ? '●' : '○'}
                    </td>
                    <td className="px-4 py-2 text-right tabular text-secondary">
                      {b.objects.toLocaleString()}
                      {b.truncated ? '+' : ''}
                    </td>
                    <td className="px-4 py-2 text-right tabular text-secondary">{fmtBytes(b.size_bytes)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border-default bg-surface-1 font-medium">
                  <td className="px-4 py-2 text-secondary" colSpan={2}>
                    {S.total}
                  </td>
                  <td className="px-4 py-2 text-right tabular text-primary">
                    {data.total_objects.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right tabular text-primary">{fmtBytes(data.total_size_bytes)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.18 — Media qayta ishlash navbati ──────────────────────── */
export function InfraQueueScreen() {
  const { data, err, reload } = useLoad<QueueStats>(fetchQueue)
  return (
    <>
      <PageHeader title={S.queue} screenId="A17.18" description={S.queueDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={1} />
        ) : (
          <div className="mx-auto max-w-md rounded-lg border border-border-default bg-surface-2 p-8 text-center">
            <p className="text-sm text-tertiary">{S.mediaJobs}</p>
            <p className="mt-2 text-5xl font-bold text-primary tabular">{data.media_jobs.toLocaleString()}</p>
            <p className="mt-2 text-sm text-secondary">
              {data.media_jobs === 0 ? S.empty : S.pending}
            </p>
          </div>
        )}
      </div>
    </>
  )
}
